import { Reveal } from "./Motion";

export const FOUNDED = 2024;

const STORY = [
  "Established in 2024, Selby Asset Group was founded with a clear vision: to make the process of sourcing and acquiring valuable assets more straightforward, transparent, and dependable.",
  "We understand that purchasing a commercial truck, trailer, marine vessel, or piece of industrial equipment is more than a simple transaction. It is an important investment in a business, a livelihood, and a future opportunity.",
  "That understanding is at the heart of everything we do.",
  "From the beginning, our focus has been on building relationships, understanding our clients' needs, and helping them navigate the asset acquisition process with greater clarity and confidence.",
  "Whether a client is searching for a dependable commercial truck, the right trailer for their operation, a boat or yacht for their next venture, or specialized construction and industrial equipment, our goal is to help connect them with suitable opportunities.",
  "We believe that great service begins with listening. Every client has different requirements, priorities, budgets, and expectations. We take those differences seriously and approach every sourcing request with care, professionalism, and attention to detail.",
  "As Selby Asset Group continues to grow, our commitment remains the same: to provide a professional, customer-focused experience built around transparency, communication, and dependable support.",
];

const COMMITMENTS = [
  ["Honest and Transparent Communication", "Providing clear information, straightforward communication, and realistic expectations throughout the sourcing process."],
  ["Understanding Every Client", "Taking the time to understand each client's goals, operational requirements, preferences, and budget."],
  ["A Personalized Approach", "Recognizing that every asset search is different and providing attention tailored to each individual request."],
  ["Professional Service", "Treating every inquiry, conversation, and business opportunity with care, respect, and professionalism."],
  ["Dependable Follow-Through", "Keeping communication open and helping clients understand the next steps as they move toward an acquisition."],
  ["Long-Term Relationships", "Striving to build lasting relationships through service, integrity, and a genuine commitment to our clients' needs."],
];

const CLIENTS = [
  "Every client relationship represents an opportunity to demonstrate what Selby Asset Group stands for.",
  "We understand that trust is earned through communication, consistency, and the way people are treated. That is why we place importance on understanding our clients, responding to their needs, and helping them make informed decisions.",
  "Our goal is for every client to feel that their needs matter, their questions are welcomed, and their asset acquisition journey is handled with professionalism and care.",
  "We are proud to build our business around the principles of service, integrity, and meaningful client relationships. As we continue to develop, we remain focused on creating positive experiences and earning the confidence of the people and businesses we serve.",
];

const VISION = [
  "Our vision is to become a trusted and recognized asset sourcing partner for individuals, entrepreneurs, and businesses seeking dependable opportunities across the commercial transportation, marine, construction, and industrial sectors.",
  "We aim to make asset sourcing more accessible, organized, and customer-focused by combining personalized service with a professional and transparent process.",
  "As we look toward the future, we remain committed to growing responsibly, strengthening client relationships, and helping our customers move forward with confidence.",
];

const MILESTONES = [
  ["2024", "Established", "Selby Asset Group is founded to make sourcing and acquiring valuable assets more straightforward, transparent, and dependable."],
  ["Today", "Four divisions", "Clients are served through dedicated Truck, Trailer, Marine, and Equipment divisions, led directly by the company's owner and manager."],
  ["Ahead", "Growing responsibly", "Strengthening client relationships and building a trusted, recognized asset sourcing partnership."],
];

export const EstablishedSeal = ({ className = "", testid = "established-seal" }) => (
  <div className={`inline-flex items-center gap-4 ${className}`} data-testid={testid}>
    <span className="relative grid place-items-center w-[4.5rem] h-[4.5rem] rounded-full border border-[rgba(212,175,55,0.55)] shrink-0">
      <span className="absolute inset-1.5 rounded-full border border-[rgba(212,175,55,0.2)]" />
      <span className="text-center leading-none">
        <span className="block font-mono text-[0.55rem] tracking-[0.25em] text-gold">EST.</span>
        <span className="block font-display text-xl text-slate-50 mt-1">{FOUNDED}</span>
      </span>
    </span>
    <span className="text-sm leading-snug">
      <span className="block text-slate-100">Established in {FOUNDED}</span>
      <span className="block text-slate-400 text-xs mt-0.5">Truck · Trailer · Marine · Equipment</span>
    </span>
  </div>
);

export const OurStory = ({ mission }) => (
  <section className="py-24 md:py-32" data-testid="about-story">
    <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-14 items-start">
      <Reveal className="lg:col-span-4 lg:sticky lg:top-32 space-y-8">
        <p className="eyebrow">Our Story</p>
        <div data-testid="about-established">
          <p className="font-mono text-xs tracking-[0.3em] text-slate-400 uppercase">Established</p>
          <p className="font-display text-[5.5rem] md:text-[7rem] leading-none text-gold mt-2">{FOUNDED}</p>
        </div>
        {mission && (
          <div className="border border-[rgba(212,175,55,0.35)] p-7 bg-[radial-gradient(ellipse_at_top_left,rgba(212,175,55,0.1),transparent_60%)]" data-testid="about-mission">
            <p className="eyebrow !text-[0.62rem]">Our mission</p>
            <p className="font-display text-2xl leading-snug mt-4 text-slate-50">{mission}</p>
          </div>
        )}
      </Reveal>
      <div className="lg:col-span-8 space-y-6" data-testid="about-body">
        {STORY.map((p, i) => (
          <Reveal key={i} delay={Math.min(i, 3) * 0.04}>
            <p className={i === 0 ? "font-display text-3xl md:text-4xl leading-snug text-slate-100" : i === 2 ? "font-display text-2xl md:text-3xl italic text-gold leading-snug" : "text-base md:text-lg text-slate-300 leading-relaxed"}>{p}</p>
          </Reveal>
        ))}
        <Reveal>
          <p className="border-l-2 border-[var(--gold)] pl-6 font-display text-2xl md:text-3xl text-slate-50 leading-snug" data-testid="about-story-closing">
            Our journey began in {FOUNDED}. Our commitment to our clients continues with every opportunity we pursue.
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);

export const Timeline = () => (
  <section className="py-20 md:py-24 border-t hairline" data-testid="about-timeline">
    <div className="max-w-7xl mx-auto px-5 md:px-10">
      <Reveal><p className="eyebrow mb-10">Our journey</p></Reveal>
      <ol className="grid md:grid-cols-3 gap-10 md:gap-0 relative">
        <span className="hidden md:block absolute left-0 right-0 top-[0.4rem] h-px bg-gradient-to-r from-[var(--gold)] via-white/15 to-transparent" aria-hidden />
        {MILESTONES.map(([when, title, text], i) => (
          <Reveal as="li" key={when} delay={i * 0.06} className="relative pl-8 md:pl-0 md:pr-10 border-l md:border-l-0 border-white/15" >
            <span className={`absolute -left-[0.4rem] md:left-0 top-0 w-3 h-3 rotate-45 ${i === 0 ? "bg-[var(--gold)]" : "border border-[var(--gold)] bg-[var(--ink)]"}`} aria-hidden />
            <p className="font-display text-4xl text-slate-50 md:mt-8" data-testid={`timeline-${i}`}>{when}</p>
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-gold mt-3">{title}</p>
            <p className="text-sm text-slate-300 leading-relaxed mt-3 max-w-sm">{text}</p>
          </Reveal>
        ))}
      </ol>
    </div>
  </section>
);

export const Commitments = () => (
  <section className="py-24 md:py-28 border-t hairline bg-[var(--ink-2)]" data-testid="about-commitments">
    <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12">
      <Reveal className="lg:col-span-4">
        <p className="eyebrow">Our commitment to clients</p>
        <h2 className="font-display text-5xl mt-5 leading-[1]">A Commitment Built on <em className="text-gold">Trust</em></h2>
        <p className="text-slate-300 leading-relaxed mt-6">At Selby Asset Group, we believe that a successful asset acquisition is about more than finding the right item. It is about creating an experience in which clients feel informed, respected, and supported throughout the process.</p>
        <p className="text-slate-400 text-sm mt-6">We are committed to:</p>
      </Reveal>
      <ol className="lg:col-span-8 grid sm:grid-cols-2 gap-px bg-white/10 border hairline self-start">
        {COMMITMENTS.map(([t, d], i) => (
          <Reveal as="li" key={t} delay={(i % 2) * 0.05} className="bg-[var(--ink-2)] p-7" >
            <span className="font-mono text-xs text-gold">0{i + 1}</span>
            <p className="font-display text-2xl text-slate-50 mt-3 leading-tight" data-testid={`commitment-${i}`}>{t}</p>
            <p className="text-sm text-slate-300 leading-relaxed mt-3">{d}</p>
          </Reveal>
        ))}
      </ol>
    </div>
  </section>
);

export const ClientsMotivation = () => (
  <section className="py-24 md:py-32 border-t hairline" data-testid="about-clients">
    <div className="max-w-5xl mx-auto px-5 md:px-10">
      <Reveal>
        <p className="eyebrow">Client relationships</p>
        <h2 className="font-display text-4xl sm:text-5xl md:text-6xl mt-5 leading-[1.02]">Our Clients. Our Commitment. <em className="text-gold">Our Motivation.</em></h2>
      </Reveal>
      <div className="grid md:grid-cols-2 gap-x-12 gap-y-6 mt-12">
        {CLIENTS.map((p, i) => (
          <Reveal key={i} delay={(i % 2) * 0.05}><p className={i === 0 ? "font-display text-2xl text-slate-100 leading-snug" : "text-base text-slate-300 leading-relaxed"}>{p}</p></Reveal>
        ))}
      </div>
      <Reveal>
        <p className="mt-14 pt-10 border-t hairline font-display text-3xl md:text-4xl text-center text-slate-50 leading-snug" data-testid="about-clients-quote">
          At Selby Asset Group, our greatest measure of success is <em className="text-gold">the confidence our clients place in us.</em>
        </p>
      </Reveal>
    </div>
  </section>
);

export const Vision = () => (
  <section className="relative py-24 md:py-32 border-t hairline overflow-hidden" data-testid="about-vision">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_80%_20%,rgba(212,175,55,0.09),transparent_55%)]" aria-hidden />
    <div className="relative max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12">
      <Reveal className="lg:col-span-5">
        <p className="eyebrow">Looking ahead</p>
        <h2 className="font-display text-5xl md:text-6xl mt-5 leading-[1]">Our Vision for <em className="text-gold">the Future</em></h2>
      </Reveal>
      <div className="lg:col-span-7 space-y-6">
        {VISION.map((p, i) => (
          <Reveal key={i} delay={i * 0.05}><p className={i === 0 ? "font-display text-2xl md:text-3xl text-slate-100 leading-snug" : "text-base md:text-lg text-slate-300 leading-relaxed"}>{p}</p></Reveal>
        ))}
      </div>
    </div>
  </section>
);
